import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Search, Clock, MapPin, Flame } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OfferingsPage = () => {
  const [temples, setTemples] = useState([]);
  const [offeringsByTemple, setOfferingsByTemple] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTemple, setActiveTemple] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [templesRes, offeringsRes] = await Promise.all([
          axios.get(`${API}/temples`),
          axios.get(`${API}/pujas?is_offering=true`)
        ]);
        const allTemples = templesRes.data;
        const allOfferings = offeringsRes.data;

        // Group offerings by temple_id
        const grouped = {};
        allOfferings.forEach(puja => {
          if (!grouped[puja.temple_id]) grouped[puja.temple_id] = [];
          grouped[puja.temple_id].push(puja);
        });

        // Only keep temples that have at least 1 offering
        const templesWithOfferings = allTemples.filter(t => grouped[t.id]?.length > 0);
        setTemples(templesWithOfferings);
        setOfferingsByTemple(grouped);
      } catch (error) {
        console.error('Error fetching offerings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtered view
  const query = searchQuery.toLowerCase();
  const filteredTemples = temples.filter(temple => {
    if (activeTemple !== 'all' && temple.id !== activeTemple) return false;
    if (!query) return true;
    const pujas = offeringsByTemple[temple.id] || [];
    return (
      temple.name.toLowerCase().includes(query) ||
      pujas.some(p => p.name.toLowerCase().includes(query))
    );
  });

  // Count totals
  const totalOfferings = Object.values(offeringsByTemple).flat().length;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-b from-purple-50/60 via-white to-white">
        {/* ── Hero ── */}
        <div className="relative bg-gradient-to-br from-[#1a0533] via-[#2d0a52] to-[#4a0080] pt-32 pb-20 overflow-hidden">
          {/* decorative orbs */}
          <div className="absolute top-10 left-1/4 w-72 h-72 bg-purple-400/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-400/10 rounded-full blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 text-purple-200 text-sm font-semibold mb-6 border border-white/20">
                🪔 Sacred Offerings
              </span>
              <h1 className="font-heading text-4xl md:text-6xl font-black text-white leading-tight mb-4">
                Temple Offerings
              </h1>
              <p className="text-purple-200 text-lg max-w-2xl mx-auto mb-8">
                Special offerings performed at sacred temples — precious rituals for blessings, protection, and divine grace.
              </p>

              {/* Stats */}
              <div className="flex items-center justify-center gap-8 text-white/70 text-sm">
                <div className="text-center">
                  <p className="text-3xl font-black text-white">{temples.length}</p>
                  <p>Temples</p>
                </div>
                <div className="w-px h-10 bg-white/20" />
                <div className="text-center">
                  <p className="text-3xl font-black text-white">{totalOfferings}</p>
                  <p>Offerings</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ── Controls ── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10 mb-10">
          <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.08)] border border-gray-100 p-4 flex flex-wrap gap-4 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search offerings or temples..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-400 text-sm"
                data-testid="offerings-search"
              />
            </div>

            {/* Temple filter tabs */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setActiveTemple('all')}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeTemple === 'all'
                    ? 'bg-purple-600 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="filter-all-temples"
              >
                All Temples
              </button>
              {temples.map(temple => (
                <button
                  key={temple.id}
                  onClick={() => setActiveTemple(temple.id)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    activeTemple === temple.id
                      ? 'bg-purple-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`filter-temple-${temple.id}`}
                >
                  {temple.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 space-y-16">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array(6).fill(0).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl h-72 animate-pulse border border-gray-100" />
              ))}
            </div>
          ) : filteredTemples.length === 0 ? (
            <div className="text-center py-24">
              <Flame className="w-16 h-16 text-gray-200 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-400 mb-2">No Offerings Found</h3>
              <p className="text-gray-400 text-sm">Try a different search or check back soon.</p>
            </div>
          ) : (
            filteredTemples.map((temple, tIdx) => {
              const pujas = (offeringsByTemple[temple.id] || []).filter(p => {
                if (!searchQuery) return true;
                return p.name.toLowerCase().includes(query) || temple.name.toLowerCase().includes(query);
              });
              if (pujas.length === 0) return null;

              return (
                <motion.div
                  key={temple.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: tIdx * 0.08 }}
                >
                  {/* Temple Header */}
                  <div className="flex items-center gap-4 mb-6">
                    <img
                      src={temple.image_url}
                      alt={temple.name}
                      className="w-14 h-14 rounded-2xl object-cover shadow-md border-2 border-purple-100"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h2 className="font-heading text-2xl font-bold text-gray-900">{temple.name}</h2>
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-600 text-xs font-bold rounded-full">
                          {pujas.length} Offerings
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{temple.location}</span>
                      </div>
                    </div>
                    <Link to={`/temples/${temple.id}`}>
                      <button className="text-sm text-purple-600 font-semibold hover:underline flex items-center gap-1">
                        View Temple <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </Link>
                  </div>

                  {/* Offering Cards */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {pujas.map((puja, idx) => (
                      <motion.div
                        key={puja.id}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.07 }}
                        className="group bg-white rounded-2xl overflow-hidden border border-purple-100/60 shadow-[0_4px_20px_rgba(100,0,180,0.06)] hover:shadow-[0_8px_32px_rgba(100,0,180,0.14)] hover:border-purple-300/60 transition-all duration-300"
                        data-testid={`offering-card-${puja.id}`}
                      >
                        {/* Image */}
                        <div className="relative h-44 overflow-hidden">
                          <img
                            src={puja.image_url || temple.image_url}
                            alt={puja.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                          {/* Offering badge */}
                          <div className="absolute top-3 left-3">
                            <span className="flex items-center gap-1 px-2.5 py-1 bg-purple-600/90 backdrop-blur-sm text-white text-xs font-bold rounded-full">
                              🪔 Offering
                            </span>
                          </div>

                          {/* Price */}
                          <div className="absolute bottom-3 right-3">
                            <span className="px-3 py-1 bg-white/95 backdrop-blur-sm text-purple-700 font-bold text-sm rounded-full shadow-sm">
                              ₹{puja.price?.toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {/* Body */}
                        <div className="p-5">
                          <span className="text-xs text-purple-500 font-semibold uppercase tracking-wide">{temple.name}</span>
                          <h3 className="font-heading text-lg font-bold text-gray-900 mt-0.5 mb-1.5 group-hover:text-purple-700 transition-colors leading-snug">
                            {puja.name}
                          </h3>
                          <p className="text-gray-500 text-sm line-clamp-2 mb-3">{puja.description}</p>

                          <div className="flex items-center gap-2 text-xs text-gray-400 mb-4">
                            <Clock className="w-3.5 h-3.5" />
                            <span>{puja.duration}</span>
                          </div>

                          {/* Benefits */}
                          {puja.benefits?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-4">
                              {puja.benefits.slice(0, 3).map((b, i) => (
                                <span key={i} className="px-2 py-0.5 bg-purple-50 text-purple-600 text-xs rounded-full">
                                  {b}
                                </span>
                              ))}
                              {puja.benefits.length > 3 && (
                                <span className="text-xs text-gray-400">+{puja.benefits.length - 3}</span>
                              )}
                            </div>
                          )}

                          {/* Book Now — same route as regular pujas */}
                          <Link to={`/temples/${temple.id}/book/${puja.id}`}>
                            <button
                              className="w-full py-2.5 rounded-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-sm transition-all"
                              data-testid={`book-offering-${puja.id}`}
                            >
                              Book This Offering
                              <ArrowRight className="w-4 h-4" />
                            </button>
                          </Link>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default OfferingsPage;
